/**
 * BetaFeedbackWidget — floating button on every page, opens a 3-field feedback modal.
 * On first quality submission auto-awards Beta Founder badge + 60 days Elite.
 */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { MessageSquare, X, Sparkles, CheckCircle2, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const HIDE_KEY = "beta_feedback_widget_hidden_until";

const BetaFeedbackWidget = () => {
  const [open, setOpen] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [form, setForm] = useState({ whats_working: "", whats_broken: "", whats_missing: "", contact_email: "" });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [myStatus, setMyStatus] = useState(null);

  const refreshStatus = useCallback(() => {
    const token = localStorage.getItem("token");
    if (!token) return Promise.resolve();
    return axios
      .get(`${API}/feedback/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setMyStatus(r.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const hideUntil = parseInt(localStorage.getItem(HIDE_KEY) || "0", 10);
    if (hideUntil > Date.now()) {
      // eslint-disable-next-line
      setHidden(true);
    }
    // Defer the status fetch out of the effect tick to avoid the
    // react-hooks/set-state-in-effect rule firing on the .then() setState.
    const t = setTimeout(refreshStatus, 0);
    return () => clearTimeout(t);
  }, [refreshStatus]);

  if (hidden) return null;

  const submit = async () => {
    const body = `${form.whats_working || ""}${form.whats_broken || ""}${form.whats_missing || ""}`.trim();
    if (body.length < 5) return;
    setSubmitting(true);
    setResult(null);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(`${API}/feedback`, {
        ...form,
        page_url: window.location.pathname,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setResult(res.data);
      if (res.data?.rewards_granted?.length > 0) {
        await refreshStatus();
      }
    } catch (e) {
      setResult({ status: "error", message: e.response?.data?.detail || "Submission failed" });
    } finally {
      setSubmitting(false);
    }
  };

  const closeModal = () => {
    setOpen(false);
    setResult(null);
    if (result?.rewards_granted?.length > 0) {
      setForm({ whats_working: "", whats_broken: "", whats_missing: "", contact_email: "" });
    }
  };

  return (
    <>
      {/* Floating Trigger Button */}
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-6 left-6 z-40 bg-orange-500 hover:bg-orange-400 text-white p-3 rounded-full shadow-lg flex items-center gap-2 transition-all hover:scale-105"
        data-testid="open-feedback-widget-btn"
        title="Beta Feedback — earn Founder rewards"
      >
        <MessageSquare className="w-5 h-5" />
        {myStatus?.beta_founder ? (
          <span className="hidden sm:inline text-xs font-mono uppercase tracking-widest font-bold">Founder ✓</span>
        ) : (
          <span className="hidden sm:inline text-xs font-mono uppercase tracking-widest font-bold">Feedback</span>
        )}
      </button>

      {/* Modal */}
      {open && (
        <div
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={closeModal}
          data-testid="feedback-modal"
        >
          <div
            className="bg-[#0A0A0A] border border-orange-500/30 max-w-lg w-full max-h-[90vh] overflow-y-auto p-6 space-y-4 rounded"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-orange-400 mb-1">
                  Beta Feedback · Founder Rewards
                </p>
                <h2 className="text-2xl font-black uppercase text-white">Help Build It</h2>
                <p className="text-xs text-zinc-400 mt-1">
                  Any quality feedback = <span className="text-orange-400 font-bold">Beta Founder badge + 60 days Elite free</span>.
                </p>
              </div>
              <button
                onClick={closeModal}
                className="text-zinc-400 hover:text-white p-1"
                data-testid="close-feedback-btn"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Reward status strip */}
            {myStatus && (
              <div className="grid grid-cols-3 gap-px bg-white/[0.06]">
                {[
                  { label: "Founder", on: myStatus.beta_founder, color: "#FF4500" },
                  { label: "Lifetime", on: myStatus.lifetime_elite, color: "#E5E4E2" },
                  { label: "Tee", on: myStatus.tshirt_winner, color: "#FFD700" },
                ].map((b) => (
                  <div
                    key={b.label}
                    className={`bg-[#050505] p-2 text-center ${b.on ? "" : "opacity-30"}`}
                    data-testid={`reward-status-${b.label.toLowerCase()}`}
                  >
                    <p className="text-[9px] font-mono uppercase tracking-widest" style={{ color: b.color }}>
                      {b.label}
                    </p>
                    <p className="text-xs font-black mt-0.5" style={{ color: b.on ? b.color : "#666" }}>
                      {b.on ? "✓ EARNED" : "—"}
                    </p>
                  </div>
                ))}
              </div>
            )}

            {result ? (
              <div className="text-center py-8 space-y-3" data-testid="feedback-result">
                {result.rewards_granted?.length > 0 ? (
                  <>
                    <Sparkles className="w-12 h-12 text-orange-400 mx-auto" />
                    <h3 className="text-2xl font-black text-orange-400">BETA FOUNDER UNLOCKED 🏀</h3>
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto" />
                    <h3 className="text-xl font-black text-white">Got it.</h3>
                  </>
                )}
                <p className="text-sm text-zinc-300">{result.message}</p>
                <button
                  onClick={closeModal}
                  className="bg-orange-500 hover:bg-orange-400 text-white text-xs font-black uppercase tracking-wider px-5 py-2"
                >
                  Close
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                <div>
                  <label className="text-[10px] font-mono uppercase tracking-widest text-emerald-400">
                    ✓ What&apos;s working
                  </label>
                  <textarea
                    value={form.whats_working}
                    onChange={(e) => setForm({ ...form, whats_working: e.target.value })}
                    rows={2}
                    placeholder="Features you love, things that feel right..."
                    className="w-full mt-1 px-3 py-2 bg-zinc-900 border border-white/[0.08] text-white text-sm focus:outline-none focus:border-emerald-400 rounded"
                    data-testid="feedback-working-input"
                  />
                </div>
                <div>
                  <label className="text-[10px] font-mono uppercase tracking-widest text-red-400">
                    ✕ What&apos;s broken
                  </label>
                  <textarea
                    value={form.whats_broken}
                    onChange={(e) => setForm({ ...form, whats_broken: e.target.value })}
                    rows={2}
                    placeholder="Bugs, confusing flows, anything that doesn't work..."
                    className="w-full mt-1 px-3 py-2 bg-zinc-900 border border-white/[0.08] text-white text-sm focus:outline-none focus:border-red-400 rounded"
                    data-testid="feedback-broken-input"
                  />
                </div>
                <div>
                  <label className="text-[10px] font-mono uppercase tracking-widest text-amber-400">
                    + What&apos;s missing
                  </label>
                  <textarea
                    value={form.whats_missing}
                    onChange={(e) => setForm({ ...form, whats_missing: e.target.value })}
                    rows={2}
                    placeholder="Features you want, ideas, your dream version..."
                    className="w-full mt-1 px-3 py-2 bg-zinc-900 border border-white/[0.08] text-white text-sm focus:outline-none focus:border-amber-400 rounded"
                    data-testid="feedback-missing-input"
                  />
                </div>
                <div>
                  <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-400">
                    Best email (for reply, optional)
                  </label>
                  <input
                    type="email"
                    value={form.contact_email}
                    onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
                    placeholder="you@example.com"
                    className="w-full mt-1 px-3 py-2 bg-zinc-900 border border-white/[0.08] text-white text-sm focus:outline-none focus:border-zinc-400 rounded"
                    data-testid="feedback-email-input"
                  />
                </div>
                <p className="text-[10px] text-zinc-500">
                  💡 30+ characters total = Beta Founder rewards. The best 5-10 get Lifetime Elite. Top 3 get the limited Beta Team tee.
                </p>
                <button
                  onClick={submit}
                  disabled={submitting}
                  className="w-full bg-orange-500 hover:bg-orange-400 disabled:opacity-50 text-white font-black text-xs uppercase tracking-wider py-3 inline-flex items-center justify-center gap-2 rounded"
                  data-testid="submit-feedback-btn"
                >
                  {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : <>Submit & Claim Rewards</>}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default BetaFeedbackWidget;
